/* PRIVILEGE HANDMADE | Linked-color previews for Prestige product cards.
 * Desktop: hover to preview, click swatch to visit color's product page.
 * Touch: tap swatch to preview; tap card image/title to visit selected product.
 * Unmodified Prestige media and custom card_image remain the default state.
 */
(() => {
  const states = new WeakMap();
  const hoverDevice = () => matchMedia('(hover: hover) and (pointer: fine)').matches;

  function getState(card) {
    let state = states.get(card);
    if (state) return state;
    const media = card.querySelector('a.product-card__media');
    const title = card.querySelector('a.product-title');
    const image = card.querySelector('.product-card__image--primary');
    const price = card.querySelector('.product-card__info price-list');
    state = {
      media, title, image,
      originalMediaHref: media?.getAttribute('href') || '',
      originalTitleHref: title?.getAttribute('href') || '',
      originalTitle: title?.textContent || '',
      originalAlt: image?.alt || '',
      originalPrice: price?.cloneNode(true) || null,
      selected: null,
      requestId: 0
    };
    states.set(card, state);
    return state;
  }

  function markActive(card, active) {
    card.querySelectorAll('.ph-card-swatch').forEach((swatch) => {
      const chosen = swatch === active || (!active && swatch.dataset.phId === card.querySelector('.ph-card-swatches')?.dataset.phBaseId);
      swatch.classList.toggle('is-active', chosen);
      if (chosen) swatch.setAttribute('aria-current', 'true');
      else swatch.removeAttribute('aria-current');
    });
  }

  function updatePrice(card, state, swatch) {
    const oldPrice = card.querySelector('.product-card__info price-list');
    if (!oldPrice) return;
    const newPrice = swatch
      ? swatch.querySelector('.ph-card-swatch__price')?.content.firstElementChild
      : state.originalPrice;
    if (newPrice) oldPrice.replaceWith(newPrice.cloneNode(true));
  }

  function restore(card) {
    const state = states.get(card);
    if (!state) return;
    ++state.requestId;
    state.selected = null;
    if (state.media) state.media.setAttribute('href', state.originalMediaHref);
    if (state.title) {
      state.title.setAttribute('href', state.originalTitleHref);
      state.title.textContent = state.originalTitle;
    }
    if (state.image) state.image.alt = state.originalAlt;
    updatePrice(card, state, null);
    markActive(card, null);
    card.classList.remove('ph-previewing');
    card.querySelectorAll('.ph-card-preview-image').forEach((node) => node.remove());
  }

  function preview(card, swatch) {
    const state = getState(card);
    if (!state.media || !state.image) return;
    const baseId = card.querySelector('.ph-card-swatches')?.dataset.phBaseId;
    if (swatch.dataset.phId === baseId) {
      if (state.selected) restore(card);
      return;
    }
    if (state.selected === swatch) return;
    state.selected = swatch;
    const requestId = ++state.requestId;
    const url = swatch.getAttribute('href');
    state.media.setAttribute('href', url);
    if (state.title) {
      state.title.setAttribute('href', url);
      state.title.textContent = swatch.dataset.phTitle || state.originalTitle;
    }
    state.image.alt = swatch.dataset.phAlt || state.originalAlt;
    updatePrice(card, state, swatch);
    markActive(card, swatch);
    card.classList.add('ph-previewing');

    const src = swatch.dataset.phImage;
    if (!src) return;
    const loaded = new Image();
    loaded.onload = () => {
      if (state.requestId !== requestId || !card.isConnected) return;
      const next = state.image.cloneNode(false);
      next.classList.remove('product-card__image--primary', 'product-card__image--secondary');
      next.classList.add('ph-card-preview-image');
      next.removeAttribute('srcset');
      next.removeAttribute('sizes');
      next.removeAttribute('loading');
      next.src = src;
      next.alt = '';
      next.setAttribute('aria-hidden', 'true');
      state.media.appendChild(next);
      // Retain the previous selected image until the next one has faded in.
      const oldLayers = [...state.media.querySelectorAll('.ph-card-preview-image')].filter((element) => element !== next);
      requestAnimationFrame(() => next.classList.add('is-visible'));
      setTimeout(() => oldLayers.forEach((element) => element.remove()), 220);
    };
    loaded.onerror = () => {
      // Keep the original image usable; links and price remain correct.
    };
    loaded.src = src;
  }

  document.addEventListener('pointerover', (event) => {
    if (!hoverDevice()) return;
    const swatch = event.target.closest?.('.ph-card-swatch');
    if (swatch) preview(swatch.closest('product-card'), swatch);
  });

  document.addEventListener('pointerout', (event) => {
    if (!hoverDevice()) return;
    const card = event.target.closest?.('product-card.ph-previewing');
    if (card && !card.contains(event.relatedTarget)) restore(card);
  });

  document.addEventListener('focusin', (event) => {
    const swatch = event.target.closest?.('.ph-card-swatch');
    if (swatch && hoverDevice()) preview(swatch.closest('product-card'), swatch);
  });

  document.addEventListener('focusout', (event) => {
    if (!hoverDevice()) return;
    const card = event.target.closest?.('product-card.ph-previewing');
    if (card && !card.contains(event.relatedTarget) && !card.matches(':hover')) restore(card);
  });

  document.addEventListener('click', (event) => {
    const swatch = event.target.closest?.('.ph-card-swatch');
    if (!swatch || hoverDevice()) return; // Desktop follows the link normally.
    // Touch interaction previews. Product photo/title leads to selected page.
    event.preventDefault();
    preview(swatch.closest('product-card'), swatch);
  });
})();